import { CategoryType } from 'types/category'
import { BreadType } from 'types/bread'
import { StockType, StockUnionType } from 'types/stock'
import { ScheduleType } from 'types/schedule'
import { StoreType } from 'types/store'
import { parseHirakanaToKanakana } from './helper'
import { DayOfWeekType, DaysOfWeek } from './daysOfWeek'
import { SpecialHolidayType } from 'types/calendar'
import { ManufacturerStoreBreadType } from 'types/storeBread'
const daysOfWeek = new DaysOfWeek()

type FilterKeyword<T> = (keyword: string, list: T[]) => T[]

export const filterBreadsByCategory = (
  categoryName: string,
  categories: CategoryType[],
  breads: BreadType[]
): BreadType[] => {
  const category = categories.find((category) => category.name === categoryName)
  if (!category) return breads
  return breads.filter((bread) => {
    if (!bread.category) return false
    return bread.category.path === category.category_ref.path
  })
}

export const filterBreadsByKeyword: FilterKeyword<BreadType> = (keyword, breads) => {
  return breads.filter((bread) => {
    const regexp = new RegExp('^(?=.*' + keyword + ').*$', 'g')
    const hirakanaRegexp = new RegExp('^(?=.*' + parseHirakanaToKanakana(keyword) + ').*$', 'g')
    const upperRegexp = new RegExp('^(?=.*' + keyword.toUpperCase() + ').*$', 'g')
    const lowerRegexp = new RegExp('^(?=.*' + keyword.toLowerCase() + ').*$', 'g')
    const isMatchByName =
      regexp.test(bread.name) ||
      hirakanaRegexp.test(bread.name) ||
      upperRegexp.test(bread.name) ||
      lowerRegexp.test(bread.name)
    const isMatchByShortName = bread.short_name
      ? regexp.test(bread.short_name) ||
        hirakanaRegexp.test(bread.short_name) ||
        upperRegexp.test(bread.short_name) ||
        lowerRegexp.test(bread.short_name)
      : false
    return isMatchByName || isMatchByShortName
  })
}

export const filterBreadsByStoreList = (
  manufacturerBreads: BreadType[],
  manufacturerStoreBreads: ManufacturerStoreBreadType[],
  manufacturerStoreList: StoreType[]
): BreadType[] => {
  return manufacturerBreads.filter((manufacturerBread) => {
    const manufacturerStoreBread = manufacturerStoreBreads.find(
      (storeBread) => storeBread.bread_ref.id === manufacturerBread.bread_ref?.id
    )
    if (!manufacturerStoreBread) return false
    const manufacturerStore = manufacturerStoreList.find(
      (store) => store.store_id === manufacturerStoreBread.store_ref.id
    )
    if (!manufacturerStore) return false
    return true
  })
}

export const filterBreadsByStatus = (breads: BreadType[], isShownPrivate: boolean): BreadType[] => {
  if (isShownPrivate) {
    return breads
  } else {
    return breads.filter((bread) => bread.status === 'public')
  }
}

export const isNotSetRetailerBread = (retailerBreads: BreadType[], manufacturerBread: BreadType): boolean => {
  const _retailerBreads = retailerBreads.filter(
    (retailerBread) => retailerBread.shared_bread_ref?.id === manufacturerBread.bread_ref?.id
  )
  return _retailerBreads.some((retailerBread) => retailerBread.status === 'private' || retailerBread.price === 0)
}

export const filterOnlyPrivateBreads = (
  manufacturerBreads: BreadType[],
  retailerBreads: BreadType[],
  isShownOnlyPrivate: boolean
): BreadType[] => {
  if (!isShownOnlyPrivate) {
    return manufacturerBreads
  } else {
    return manufacturerBreads.filter((manufacturerBread) => isNotSetRetailerBread(retailerBreads, manufacturerBread))
  }
}

export const filterBreadsByPage = (
  page: 'released' | 'unreleased',
  breads: BreadType[],
  stocks: StockType[]
): BreadType[] => {
  const _breads: BreadType[] = []
  // 販売中か未販売のタブに応じて商品を絞り込む
  for (let i = 0; i < breads.length; i = (i + 1) | 0) {
    const bread = breads[i]
    // 在庫データが存在しており、前営業日より前に削除されていない商品であれば「在庫あり」と見なす
    const hasStock = stocks.some((stock) => stock.bread_ref.id === bread.bread_ref?.id && !stock.is_deleted)
    // 販売中のページでは「在庫あり」の商品を表示
    if (page === 'released' && hasStock) {
      _breads.push(bread)
      // 未販売のページでは「在庫なし」の商品を表示
    } else if (page === 'unreleased' && !hasStock) {
      _breads.push(bread)
    }
  }
  return _breads
}

// 商品の事前予約/当日取り置きが(schedules)が設定済みかどうか判定して絞り込む
export const filterBreadsBySchedule = (breads: BreadType[], schedules: ScheduleType[]): BreadType[] => {
  return breads.filter((bread) => {
    return schedules.some((schedule) => schedule.bread_ref.id === bread.bread_ref?.id)
  })
}

// 商品の事前予約/当日取り置きが(stocks)が存在するかどうか判定して絞り込む
export const filterBreadsByStocks = (breads: BreadType[], stocks: StockType[]): BreadType[] => {
  return breads.filter((bread) => {
    return stocks.some((stock) => stock.bread_ref.id === bread.bread_ref?.id)
  })
}

// 商品の事前予約/当日取り置きが(schedules)が設定済みかどうか判定して絞り込む
export const filterBreadsByScheduleOrStocks = (
  breads: BreadType[],
  schedules: ScheduleType[],
  stocks: StockType[]
): BreadType[] => {
  return breads.filter((bread) => {
    return (
      schedules.some((schedule) => schedule.bread_ref.id === bread.bread_ref?.id) ||
      stocks.some((stock) => stock.bread_ref.id === bread.bread_ref?.id)
    )
  })
}

export const filterBreadsByType = (breads: BreadType[], breadTypes: StockUnionType[]): BreadType[] => {
  return breads
    .filter((bread) => {
      // FIXME Marché Bakery専用商品を非表示にしているが、ダセルーノでどの種別も設定されていない商品を作成できるので対応が必要
      return !(bread.is_type_shared && !bread.is_type_daily && !bread.is_type_preorder && !bread.is_type_delivery)
    })
    .filter((bread) => {
      return (
        // 「すべて」が選択されている
        (breadTypes.includes('daily') && breadTypes.includes('preorder') && breadTypes.includes('delivery')) ||
        // 特定の種別が選択されている
        breadTypes.some((type) => {
          return (
            (type === 'daily' && bread.is_type_daily) ||
            (type === 'preorder' && bread.is_type_preorder) ||
            (type === 'delivery' && bread.is_type_delivery)
          )
        })
      )
    })
}

export const filterBreadsOnThisDay = (
  breads: BreadType[],
  schedules: ScheduleType[],
  date: Date,
  specialHolidays: SpecialHolidayType[]
): BreadType[] => {
  return breads.filter((bread) => {
    return schedules
      .filter((schedule) => schedule.bread_ref.id === bread.bread_ref?.id)
      .some((schedule) => schedule.days_of_week.includes(daysOfWeek.getDayOfWeek(date, specialHolidays)))
  })
}

export const filterBreadsByCurrentTab = ({
  breads,
  checkedOrderTypes,
  currentTab,
  dayOfWeek,
  schedules,
  stocks,
}: {
  breads: BreadType[]
  checkedOrderTypes: string[]
  currentTab: 'released' | 'unreleased'
  dayOfWeek: DayOfWeekType
  schedules: ScheduleType[]
  stocks: StockType[]
}) => {
  /**
   * 当日取り置き : 自動配信で生成予定および都度配信で手動設定済
   * 事前予約 : 在庫設定済
   */
  if (currentTab === 'released') {
    return breads.filter((bread) =>
      schedules.some((schedule) => {
        if (schedule.bread_ref.id !== bread.bread_ref?.id) return false
        if (schedule.type === 'daily') {
          if (!checkedOrderTypes.includes('daily')) {
            return false
          }
          const dailyStocks = stocks.filter(
            (stock) => schedule.bread_ref.id === stock.bread_ref.id && stock.type === 'daily'
          )
          // 当日取り置き : 自動配信で生成予定、自動配信で生成済、都度配信で手動設定済
          return (
            (schedule.days_of_week.includes(dayOfWeek) && dailyStocks.length === 0) ||
            dailyStocks.some((stock) => stock.stock > 0)
          )
        } else if (schedule.type === 'preorder') {
          if (!checkedOrderTypes.includes('preorder')) {
            return false
          }
          const preorderStocks = stocks.filter(
            (stock) => schedule.bread_ref.id === stock.bread_ref.id && stock.type === 'preorder'
          )
          // 事前予約 : 在庫設定済
          return preorderStocks.length > 0
        } else {
          return false
        }
      })
    )
  } else if (currentTab === 'unreleased') {
    /**
     * 当日取り置き :  在庫が削除（ゼロ）されているもしくは都度配信で未設定
     * 事前予約 : 在庫未設定
     */
    return breads.filter((bread) =>
      schedules.some((schedule) => {
        if (schedule.bread_ref.id !== bread.bread_ref?.id) return false
        if (schedule.type === 'daily') {
          if (!checkedOrderTypes.includes('daily')) {
            return false
          }
          const dailyStocks = stocks.filter(
            (stock) => schedule.bread_ref.id === stock.bread_ref.id && stock.type === 'daily'
          )
          // 当日取り置き :  在庫が削除（ゼロ）されているもしくは都度配信で未設定
          return (
            dailyStocks.every((stock) => stock.stock === 0) ||
            (!schedule.days_of_week.includes(dayOfWeek) && dailyStocks.length === 0)
          )
        } else if (schedule.type === 'preorder') {
          if (!checkedOrderTypes.includes('preorder')) {
            return false
          }
          const preorderStocks = stocks.filter(
            (stock) => schedule.bread_ref.id === stock.bread_ref.id && stock.type === 'preorder'
          )
          // 事前予約 : 在庫未設定
          return preorderStocks.length === 0
        } else {
          return false
        }
      })
    )
  } else {
    return breads
  }
}

export const filterStoresByKeyword: FilterKeyword<StoreType> = (keyword, stores) => {
  return stores.filter((store) => {
    const regexp = new RegExp('^(?=.*' + keyword + ').*$', 'g')
    const hirakanaRegexp = new RegExp('^(?=.*' + parseHirakanaToKanakana(keyword) + ').*$', 'g')
    const upperRegexp = new RegExp('^(?=.*' + keyword.toUpperCase() + ').*$', 'g')
    const lowerRegexp = new RegExp('^(?=.*' + keyword.toLowerCase() + ').*$', 'g')
    return (
      regexp.test(store.name) ||
      regexp.test(store.name_kana) ||
      hirakanaRegexp.test(store.name_kana) ||
      upperRegexp.test(store.name.toUpperCase()) ||
      upperRegexp.test(store.name_kana.toUpperCase()) ||
      lowerRegexp.test(store.name.toLowerCase()) ||
      lowerRegexp.test(store.name_kana.toLowerCase())
    )
  })
}

export const filterStoresByStockTypes = (breadTypes: StockUnionType[], stores: StoreType[]): StoreType[] => {
  return stores.filter((store) => {
    return breadTypes.every(
      (breadType) => store.has_schedule_types.includes(breadType) && !store.has_stock_types.includes(breadType)
    )
  })
}
